import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  ThemeProvider,
  SxProps,
  Theme,
} from "@mui/material";
import { baseTheme } from "mui-theme";
import {
  useTimezoneSelect,
  allTimezones,
  type ITimezoneOption,
  type TimezoneSelectOptions,
} from "react-timezone-select";

interface TimezoneSelectProps {
  value: string | ITimezoneOption;
  onChange: (timezone: ITimezoneOption) => void;
  label?: string;
  labelStyle?: TimezoneSelectOptions["labelStyle"];
  timezones?: TimezoneSelectOptions["timezones"];
  sx?: SxProps<Theme>;
  disabled?: boolean;
}

// Convert to Functional Component
const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  labelStyle = "original",
  timezones = allTimezones,
  sx,
  disabled = false,
}) => {
  const { options, parseTimezone } = useTimezoneSelect({
    labelStyle,
    timezones,
  });

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    const timezoneObject = parseTimezone(selectedValue);
    onChange(timezoneObject);
  };

  const currentValue = typeof value === "string" ? value : value?.value || "";

  return (
    <ThemeProvider theme={baseTheme}>
      <FormControl fullWidth sx={sx} disabled={disabled}>
        <InputLabel id="timezone-select-label">Time zone</InputLabel>
        <Select
          labelId="timezone-select-label"
          id="timezone-select"
          value={currentValue}
          label="Time zone"
          onChange={handleChange}
          variant="outlined"
          name="timezone"
          sx={{ height: "56px" }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} color="primary">
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ThemeProvider>
  );
};

export default TimezoneSelect;
